import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TableSkeleton } from "@/components/skeleton/TableSkeleton";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import { formatMoney } from "@/core/pharmacy/format";
import {
  clearProductPrice,
  getAllProductPrices,
  setProductPrice,
} from "@/core/pharmacy/pharmacy-store";
import type { StockProduct } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

interface ArticleRow {
  product: StockProduct;
  stock: number;
  price: number | null;
}

function isApiOfflineError(err: unknown): boolean {
  return err instanceof IscApiError && err.status === 0;
}

export function PharmacyStockArticlesPage() {
  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiOffline, setApiOffline] = useState(false);
  const [search, setSearch] = useState("");
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [savedId, setSavedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setApiOffline(false);
    setError(null);
    try {
      const [products, balances] = await Promise.all([
        pharmacyInventoryApi.listPharmacyProducts(),
        pharmacyInventoryApi.listPharmacyStockBalances().catch(() => []),
      ]);
      const stockMap = new Map(balances.map((b) => [b.productId, b.quantityOnHand]));
      const prices = getAllProductPrices();
      const nextRows = products
        .filter((p) => p.status === "ACTIVE")
        .map((product) => ({
          product,
          stock: stockMap.get(product.id) ?? 0,
          price: prices[product.id] ?? null,
        }))
        .sort((a, b) => a.product.label.localeCompare(b.product.label, "fr"));
      setRows(nextRows);
      const drafts: Record<string, string> = {};
      for (const row of nextRows) {
        drafts[row.product.id] =
          row.price !== null && row.price > 0 ? String(row.price) : "";
      }
      setPriceDrafts(drafts);
    } catch (err) {
      setApiOffline(isApiOfflineError(err));
      setError(
        isApiOfflineError(err)
          ? "API stock inaccessible."
          : err instanceof Error
            ? err.message
            : "Chargement impossible.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      ({ product }) =>
        product.label.toLowerCase().includes(q) ||
        product.sku.toLowerCase().includes(q) ||
        product.code.toLowerCase().includes(q) ||
        (product.barcode?.toLowerCase().includes(q) ?? false),
    );
  }, [rows, search]);

  const pagination = useClientPagination(filtered, [search], 25);
  const { pagedItems } = pagination;

  const pricedCount = useMemo(
    () => rows.filter((r) => r.price !== null && r.price > 0).length,
    [rows],
  );

  const savePrice = (productId: string) => {
    const raw = priceDrafts[productId]?.trim() ?? "";
    const price = Number(raw);
    if (!raw || price <= 0) {
      clearProductPrice(productId);
      setRows((prev) =>
        prev.map((row) =>
          row.product.id === productId ? { ...row, price: null } : row,
        ),
      );
      setPriceDrafts((prev) => ({ ...prev, [productId]: "" }));
      return;
    }
    setProductPrice(productId, price);
    setRows((prev) =>
      prev.map((row) => (row.product.id === productId ? { ...row, price } : row)),
    );
    setSavedId(productId);
    window.setTimeout(() => setSavedId(null), 1500);
  };

  const handlePriceKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    productId: string,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      savePrice(productId);
    }
  };

  return (
    <div>
      <PageHeader
        title="Articles médicaments"
        description="Catalogue pharmacie — définissez le prix officine une fois, réutilisé à la facture et au scan."
        actions={
          <div className="d-flex gap-2 ph-page-header-actions">
            <Link to={routes.pharmacyStockScan} className="btn btn-outline-secondary btn-sm">
              Scan
            </Link>
            <Link to={routes.pharmacyInvoice} className="btn btn-primary btn-sm">
              Nouvelle facture
            </Link>
          </div>
        }
      />

      {error && (
        <div className="alert alert-warning py-2">
          {error}
          {apiOffline && (
            <span className="ms-1 small">
              Lancez <code>pnpm dev:api</code> dans <code>itaas-inventory</code>.
            </span>
          )}
        </div>
      )}

      <div className="ph-invoice-sheet mb-3">
        <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between ph-stock-list-head">
          <div style={{ minWidth: "16rem", flex: "1 1 240px" }}>
            <label className="form-label small fw-semibold mb-1" htmlFor="art-search">
              Rechercher
            </label>
            <input
              id="art-search"
              type="search"
              className="form-control form-control-sm"
              placeholder="Nom, SKU, code-barres…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="small text-muted">
            {filtered.length} article{filtered.length !== 1 ? "s" : ""} · {pricedCount} avec prix
            officine
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => void load()}
            disabled={loading}
          >
            Actualiser
          </button>
        </div>
      </div>

      <div className="ph-invoice-sheet">
        {loading ? (
          <TableSkeleton rows={10} columns={7} />
        ) : filtered.length === 0 ? (
          <p className="text-muted mb-0">Aucun article trouvé.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm ph-dash-table ph-diag-table align-middle mb-0">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Libellé</th>
                  <th>Code-barres</th>
                  <th>Unité</th>
                  <th className="text-end">Stock</th>
                  <th className="text-end ph-price-col">Prix officine</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(({ product, stock, price }) => (
                  <tr key={product.id} className="ph-diag-row">
                    <td data-label="SKU">
                      <code className="small">{product.sku}</code>
                    </td>
                    <td className="fw-medium" data-label="Libellé">
                      {product.label}
                    </td>
                    <td className="small text-muted" data-label="Code-barres">
                      {product.barcode ?? "—"}
                    </td>
                    <td className="small" data-label="Unité">
                      {product.unit}
                    </td>
                    <td className="text-end" data-label="Stock">
                      <span className={stock <= 0 ? "text-danger fw-semibold" : ""}>
                        {stock}
                      </span>
                    </td>
                    <td className="text-end ph-price-col" data-label="Prix officine">
                      <div className="d-inline-flex align-items-center gap-1 justify-content-end">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm ph-price-input"
                          placeholder="0.00"
                          value={priceDrafts[product.id] ?? ""}
                          onChange={(e) =>
                            setPriceDrafts((prev) => ({
                              ...prev,
                              [product.id]: e.target.value,
                            }))
                          }
                          onBlur={() => savePrice(product.id)}
                          onKeyDown={(e) => handlePriceKeyDown(e, product.id)}
                          aria-label={`Prix pour ${product.label}`}
                        />
                        {savedId === product.id && (
                          <span className="small text-success">OK</span>
                        )}
                        {savedId !== product.id && price !== null && price > 0 && (
                          <span className="small text-muted d-none d-md-inline">
                            {formatMoney(price)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-end ph-diag-no-label" data-label="">
                      <Link
                        to={`${routes.pharmacyInvoice}?productId=${product.id}`}
                        className="btn btn-link btn-sm p-0"
                      >
                        Facturer
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>

      <p className="small text-muted mt-3 mb-0">
        Le prix est enregistré localement pour la pharmacie et pré-rempli à la facture. Source
        stock : API inventory.
      </p>
    </div>
  );
}
